import { MouseEventHandler, ReactNode, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './AlertForm.scss';
import { MdCheck } from 'react-icons/md';
import * as R from 'ramda';
import { parseISO } from 'date-fns';
import {
  greaterThanIgnoreTime,
  lessThanIgnoreTime
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { ValidateSql } from '@blisspointmedia/bpm-types/dist/AnomalyDetection';
import { Button } from '@Components/Button/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Size } from '@Components/designConstants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { AlertFormData, BudgetOption, PresetSelectOption } from '../anomalyDetectionConstants';
import { FrequencySection } from '../FrequencySection';
import { matchOptionValue, matchOptionValues, SECTION_CHECKBOX_PROPS } from '../utils/alertUtils';

interface AlertFormProps {
  formData: AlertFormData;
  onFormDataChange: (formData: AlertFormData) => void;
  budgetOptions: BudgetOption[];
  emailAddressOptions: { value: string; label: string }[];
  slackMemberOptions: { value: string; label: string }[];
  slackChannelOptions: { value: string; label: string }[];
  usedBudgetIds: Set<number>;
  presetOptions: PresetSelectOption[];
  selectedPresetId?: string | null;
  onPresetChange?: (presetId: string | null) => void;
  isLoadingBudgets: boolean;
  isLoadingEmailAddresses: boolean;
  isLoadingSlackMembers: boolean;
  isLoadingSlackChannels: boolean;
  isLoadingPresets: boolean;
  budgetError: unknown;
  emailAddressError: unknown;
  slackMembersError: unknown;
  slackChannelsError: unknown;
  presetsError: unknown;
  onSqlValidationChange?: (isValid: boolean) => void;
  initialSqlValid?: boolean;
  fieldErrors?: Record<string, string>;
}

export const AlertForm = ({
  formData,
  onFormDataChange,
  budgetOptions,
  emailAddressOptions,
  slackMemberOptions,
  slackChannelOptions,
  usedBudgetIds,
  presetOptions,
  selectedPresetId,
  onPresetChange,
  isLoadingBudgets,
  isLoadingEmailAddresses,
  isLoadingSlackMembers,
  isLoadingSlackChannels,
  isLoadingPresets,
  budgetError,
  emailAddressError,
  slackMembersError,
  slackChannelsError,
  presetsError,
  onSqlValidationChange,
  initialSqlValid = false,
  fieldErrors = {}
}: AlertFormProps) => {
  const spendCadenceOptions = [{ value: 'Evenly distributed', label: 'Evenly distributed' }];

  const [isValidatingSql, setIsValidatingSql] = useState(false);
  const [sqlValidationSuccess, setSqlValidationSuccess] = useState(initialSqlValid);
  const [sqlValidationResult, setSqlValidationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    setSqlValidationSuccess(initialSqlValid);
  }, [initialSqlValid]);

  const handleInputChange = (section: string, field: string, value: string | boolean) => {
    const newFormData = R.clone(formData);
    const data = newFormData as unknown as Record<string, unknown>;

    if (section.includes('.')) {
      const [parentSection, childSection] = section.split('.');
      const parentData = data[parentSection] as Record<string, unknown>;
      data[parentSection] = {
        ...parentData,
        [childSection]: {
          ...(parentData[childSection] as Record<string, unknown>),
          [field]: value
        }
      };
    } else {
      data[section] = {
        ...(data[section] as Record<string, unknown>),
        [field]: value
      };
    }

    onFormDataChange(newFormData);
  };

  const handleCheckboxChange = (section: string, enabled: boolean) => {
    const newFormData = R.clone(formData);
    const data = newFormData as unknown as Record<string, unknown>;
    data[section] = {
      ...(data[section] as Record<string, unknown>),
      enabled
    };
    onFormDataChange(newFormData);
  };

  const handleSimpleInputChange = (field: string, value: string | boolean) => {
    const newFormData = R.clone({ ...formData, [field]: value });
    onFormDataChange(newFormData);

    if (field === 'customSql' && sqlValidationSuccess) {
      setSqlValidationSuccess(false);
      setSqlValidationResult(null);
      onSqlValidationChange?.(false);
    }
  };

  const handleValidateSql: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    event.stopPropagation();

    if (!formData.customSql || !formData.customSql.trim()) {
      setSqlValidationResult({
        success: false,
        message: 'Please enter a SQL query to validate'
      });
      return;
    }

    setIsValidatingSql(true);
    setSqlValidationResult(null);

    const result = ValidateSql(formData.customSql.trim());

    setSqlValidationResult({
      success: result.isValid,
      message: result.isValid ? 'SQL query is valid' : result.error || 'SQL validation failed'
    });
    setSqlValidationSuccess(result.isValid);
    onSqlValidationChange?.(result.isValid);

    setIsValidatingSql(false);
  };

  let sqlButtonLabel: ReactNode = 'Check SQL';
  if (isValidatingSql) {
    sqlButtonLabel = 'Validating...';
  } else if (sqlValidationSuccess) {
    sqlButtonLabel = (
      <>
        <MdCheck className='check-icon' />
        Success
      </>
    );
  }

  const isBudgetDateOutsideRange = (date: string): boolean => {
    const selectedBudget = budgetOptions.find(opt => parseInt(opt.value, 10) === formData.budgetId);
    const budgetData = selectedBudget?.rowData;

    if (!budgetData?.start_date || !budgetData?.end_date) {
      return false;
    }

    const budgetStartDate = parseISO(budgetData.start_date);
    const budgetEndDate = parseISO(budgetData.end_date);

    return (
      lessThanIgnoreTime(parseISO(date), budgetStartDate, false) ||
      greaterThanIgnoreTime(parseISO(date), budgetEndDate, false)
    );
  };

  const isCustomDateOutsideRange = (date: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parseISO(date) < today;
  };

  let isOutsideRange: ((date: string) => boolean) | undefined;
  if (formData.alertType === 'budget') {
    isOutsideRange = isBudgetDateOutsideRange;
  } else if (formData.alertType === 'custom') {
    isOutsideRange = isCustomDateOutsideRange;
  }

  // Dropdown formatOptionLabel callback; not a component definition despite returning JSX
  const formatBudgetOptionLabel = (
    option: { label: string; value: string },
    meta?: { context: 'menu' | 'value' }
  ): ReactNode => {
    const budgetId = parseInt(option.value, 10);
    if (meta?.context === 'menu' && usedBudgetIds.has(budgetId)) {
      return <div className='disabled-budget-option'>{option.label} (Already has alerts)</div>;
    }
    return option.label;
  };

  return (
    <Form>
      <div className='form-section'>
        <h4 className='section-title'>
          Alert Name<span className='asterisk'>*</span>
        </h4>
        <Form.Group>
          <Form.Control
            type='text'
            value={formData.alertName}
            onChange={e => handleSimpleInputChange('alertName', e.target.value)}
            placeholder='Enter alert name...'
            className='form-control'
          />
          {fieldErrors?.alertName && (
            <small className='form-text text-danger'>{fieldErrors.alertName}</small>
          )}
        </Form.Group>
      </div>

      <div className='form-section'>
        <h4 className='section-title'>
          Alert Type<span className='asterisk'>*</span>
        </h4>
        <Form.Group>
          <Dropdown
            searchable
            ariaLabel='Alert type'
            placeholder='Select alert type...'
            options={[
              { value: 'budget', label: 'Budget' },
              { value: 'custom', label: 'Custom SQL' }
            ]}
            value={formData.alertType}
            onChange={alertType => handleSimpleInputChange('alertType', alertType ?? 'budget')}
            reactSelectProps={{ noOptionsMessage: () => 'No alert types available' }}
          />
        </Form.Group>
      </div>

      {formData.alertType === 'budget' && (
        <>
          <div className='form-section'>
            <div className='section-header'>
              <h4 className='section-title'>
                Budget<span className='asterisk'>*</span>
              </h4>
            </div>
            <Form.Group>
              {isLoadingBudgets && (
                <div className='loading-indicator'>
                  <small className='form-text text-muted'>Loading budgets...</small>
                </div>
              )}
              {Boolean(budgetError) && (
                <div className='error-message'>
                  <small className='form-text text-danger'>
                    Failed to load budgets. Using fallback options.
                  </small>
                </div>
              )}
              <Dropdown
                searchable
                clearable
                ariaLabel='Budget'
                placeholder='Select budget...'
                options={budgetOptions.map(({ value, label }) => ({
                  value,
                  label,
                  disabled: usedBudgetIds.has(parseInt(value, 10))
                }))}
                value={
                  formData.budgetId > 0
                    ? (budgetOptions.find(
                        option => parseInt(option.value, 10) === formData.budgetId
                      )?.value ?? null)
                    : null
                }
                onChange={budgetValue => {
                  const budgetId = budgetValue ? parseInt(budgetValue, 10) : 0;
                  const newFormData = R.clone({ ...formData, budgetId });
                  onFormDataChange(newFormData);
                }}
                disabled={isLoadingBudgets}
                formatOptionLabel={formatBudgetOptionLabel}
                reactSelectProps={{ noOptionsMessage: () => 'No active budgets' }}
              />
              {fieldErrors?.budgetId && (
                <small className='form-text text-danger'>{fieldErrors.budgetId}</small>
              )}
            </Form.Group>
          </div>

          <div className='form-section'>
            <h4 className='section-title'>Presets</h4>
            <p className='section-description'>
              Load a saved preset to quickly configure your alert settings
            </p>
            <Form.Group>
              {isLoadingPresets && (
                <div className='loading-indicator'>
                  <small className='form-text text-muted'>Loading presets...</small>
                </div>
              )}
              {Boolean(presetsError) && (
                <div className='error-message'>
                  <small className='form-text text-danger'>
                    Failed to load presets. Using fallback options.
                  </small>
                </div>
              )}
              <Dropdown
                searchable
                clearable
                ariaLabel='Preset'
                placeholder='Select a preset...'
                options={presetOptions}
                value={matchOptionValue(presetOptions, selectedPresetId)}
                onChange={presetId => {
                  const selectedPreset = presetOptions.find(option => option.value === presetId);
                  if (selectedPreset) {
                    const preservedFormData = {
                      ...selectedPreset.presetData,
                      alertName: formData.alertName,
                      budgetId: formData.budgetId,
                      startDate: formData.startDate,
                      endDate: formData.endDate
                    };
                    onFormDataChange(preservedFormData);
                    if (onPresetChange) {
                      onPresetChange(selectedPreset.value);
                    }
                  } else if (onPresetChange) {
                    onPresetChange(null);
                  }
                }}
                disabled={isLoadingPresets}
                reactSelectProps={{ noOptionsMessage: () => 'Created presets will appear here' }}
              />
            </Form.Group>
          </div>

          <div className='form-section'>
            <div className='horizontal-line' />
          </div>

          <div className='form-section'>
            <h4 className='section-title'>
              Spend Cadence<span className='asterisk'>*</span>
            </h4>
            <p className='section-description'>
              Currently only supporting <strong>evenly distributed</strong> spend
            </p>
            <Form.Group>
              <Dropdown
                searchable
                ariaLabel='Spend cadence'
                placeholder='Select spend cadence...'
                options={spendCadenceOptions}
                value={matchOptionValue(spendCadenceOptions, formData.spendCadence)}
                onChange={spendCadence =>
                  handleSimpleInputChange('spendCadence', spendCadence ?? '')
                }
                reactSelectProps={{ noOptionsMessage: () => 'No spend cadence options available' }}
              />
            </Form.Group>
          </div>

          {fieldErrors?.monitoringType && (
            <div className='form-section'>
              <small className='form-text text-danger'>{fieldErrors.monitoringType}</small>
            </div>
          )}

          <div className='form-section'>
            <div className='section-header'>
              <Checkbox
                checked={formData.actual.enabled}
                onChange={e => handleCheckboxChange('actual', e.target.checked)}
                {...SECTION_CHECKBOX_PROPS}
              />
              <h4 className='section-title'>Actual</h4>
            </div>
            <p className='section-description'>
              Monitor actual spend performance against budget pacing expectations
            </p>
            {formData.actual.enabled && (
              <>
                <div className='threshold-inputs'>
                  <Form.Group>
                    <Form.Label>Under:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.actual.under.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.actual;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('actual', 'under', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='20'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.actual.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('actual', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount below expected (ex: 20% below 100% or $100 below goal amount)
                    </small>
                    {fieldErrors?.actualUnder && (
                      <div>
                        <small className='form-text text-danger'>{fieldErrors.actualUnder}</small>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Over:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.actual.over.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.actual;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('actual', 'over', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='20'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.actual.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('actual', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount above expected (ex: 20% above 100% or $100 above goal amount)
                    </small>
                    {fieldErrors?.actualOver && (
                      <div>
                        <small className='form-text text-danger'>{fieldErrors.actualOver}</small>
                      </div>
                    )}
                  </Form.Group>
                </div>
                <div className='budget-type-toggle'>
                  <Form.Group>
                    <div className='budget-type-toggle-width'>
                      <SegmentedControlButton
                        options={['Total Budget', 'Ideal Spend']}
                        selectedOption={
                          formData.actual.budgetType === 'total' ? 'Total Budget' : 'Ideal Spend'
                        }
                        onChange={value => {
                          const budgetType = value === 'Total Budget' ? 'total' : 'ideal';
                          handleInputChange('actual', 'budgetType', budgetType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                  </Form.Group>
                </div>
                <div className='frequency-section'>
                  <Form.Group>
                    <FrequencySection
                      frequencyData={formData.actual.frequency}
                      onFrequencyChange={(field, value) =>
                        handleInputChange('actual.frequency', field, value)
                      }
                      label='Frequency Type:'
                    />
                  </Form.Group>
                </div>
                <div className='always-notify'>
                  <Form.Group>
                    <Checkbox
                      checked={formData.actual.alwaysNotify}
                      onChange={e => handleInputChange('actual', 'alwaysNotify', e.target.checked)}
                      label='Always Notify (get alerted with a status even when the checks pass)'
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </div>

          <div className='form-section'>
            <div className='section-header'>
              <Checkbox
                checked={formData.projected.enabled}
                onChange={e => handleCheckboxChange('projected', e.target.checked)}
                {...SECTION_CHECKBOX_PROPS}
              />
              <h4 className='section-title'>Projected</h4>
            </div>
            <p className='section-description'>
              Track projected spend based on current delivery patterns and trends
            </p>
            {formData.projected.enabled && (
              <>
                <div className='threshold-inputs'>
                  <Form.Group>
                    <Form.Label>Under:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.projected.under.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.projected;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('projected', 'under', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='20'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.projected.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('projected', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount below expected (ex: 20% below 100% or $100 below goal amount)
                    </small>
                    {fieldErrors?.projectedUnder && (
                      <div>
                        <small className='form-text text-danger'>
                          {fieldErrors.projectedUnder}
                        </small>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Over:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.projected.over.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.projected;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('projected', 'over', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='20'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.projected.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('projected', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount above expected (ex: 20% above 100% or $100 below goal amount)
                    </small>
                    {fieldErrors?.projectedOver && (
                      <div>
                        <small className='form-text text-danger'>{fieldErrors.projectedOver}</small>
                      </div>
                    )}
                  </Form.Group>
                  <div className='frequency-section'>
                    <Form.Group>
                      <FrequencySection
                        frequencyData={formData.projected.frequency}
                        onFrequencyChange={(field, value) =>
                          handleInputChange('projected.frequency', field, value)
                        }
                        label='Frequency Type:'
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className='always-notify'>
                  <Form.Group>
                    <Checkbox
                      checked={formData.projected.alwaysNotify}
                      onChange={e =>
                        handleInputChange('projected', 'alwaysNotify', e.target.checked)
                      }
                      label='Always Notify (get alerted with a status even when the checks pass)'
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </div>

          <div className='form-section'>
            <div className='section-header'>
              <Checkbox
                checked={formData.remainder.enabled}
                onChange={e => handleCheckboxChange('remainder', e.target.checked)}
                {...SECTION_CHECKBOX_PROPS}
              />
              <h4 className='section-title'>Remainder</h4>
            </div>
            <p className='section-description'>
              Monitor remaining budget allocation and pacing to goal completion
            </p>
            {formData.remainder.enabled && (
              <>
                <div className='threshold-inputs'>
                  <Form.Group>
                    <Form.Label>Under:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.remainder.under.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.remainder;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('remainder', 'under', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='50'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.remainder.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('remainder', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount below expected (ex: 20% below 100% or $100 below goal amount)
                    </small>
                    {fieldErrors?.remainderUnder && (
                      <div>
                        <small className='form-text text-danger'>
                          {fieldErrors.remainderUnder}
                        </small>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Over:</Form.Label>
                    <div className='threshold-input-with-toggle'>
                      <Form.Control
                        type='number'
                        value={formData.remainder.over.replace(/[^0-9]/g, '')}
                        onChange={e => {
                          const { value } = e.target;
                          const { thresholdType } = formData.remainder;
                          const suffix = thresholdType === 'dollar' ? '$' : '%';
                          handleInputChange('remainder', 'over', `${value}${suffix}`);
                        }}
                        className='threshold-number-input'
                        placeholder='100'
                      />
                      <SegmentedControlButton
                        options={['$', '%']}
                        selectedOption={formData.remainder.thresholdType === 'dollar' ? '$' : '%'}
                        onChange={value => {
                          const thresholdType = value === '$' ? 'dollar' : 'percentage';
                          handleInputChange('remainder', 'thresholdType', thresholdType);
                        }}
                        size={Size.SM}
                      />
                    </div>
                    <small className='form-text text-muted'>
                      The amount above expected (ex: 20% above 100% or $100 above goal amount)
                    </small>
                    {fieldErrors?.remainderOver && (
                      <div>
                        <small className='form-text text-danger'>{fieldErrors.remainderOver}</small>
                      </div>
                    )}
                  </Form.Group>
                  <div className='frequency-section'>
                    <Form.Group>
                      <FrequencySection
                        frequencyData={formData.remainder.frequency}
                        onFrequencyChange={(field, value) =>
                          handleInputChange('remainder.frequency', field, value)
                        }
                        label='Frequency Type:'
                      />
                    </Form.Group>
                  </div>
                </div>
                <div className='always-notify'>
                  <Form.Group>
                    <Checkbox
                      checked={formData.remainder.alwaysNotify}
                      onChange={e =>
                        handleInputChange('remainder', 'alwaysNotify', e.target.checked)
                      }
                      label='Always Notify (get alerted with a status even when the checks pass)'
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {formData.alertType === 'custom' && (
        <div className='form-section'>
          <h4 className='section-title'>
            Custom SQL<span className='asterisk'>*</span>
          </h4>
          <p className='section-description'>
            Enter your custom SQL query for the alert. The query should return a boolean value. A
            false value will trigger an alert.
          </p>
          <Form.Group>
            <Form.Control
              as='textarea'
              rows={6}
              value={formData.customSql}
              onChange={e => handleSimpleInputChange('customSql', e.target.value)}
              placeholder="SELECT CASE WHEN SUM(amount) > 1000 THEN true ELSE false END FROM transactions WHERE date >= CURRENT_DATE - INTERVAL '7 days'"
              className='form-control'
            />
            {fieldErrors?.customSql && (
              <small className='form-text text-danger'>{fieldErrors.customSql}</small>
            )}
          </Form.Group>
          <Form.Group>
            <Button
              onClick={handleValidateSql}
              appearance={Appearance.OUTLINED}
              size={Size.SM}
              type='button'
              disabled={
                !formData.customSql ||
                !formData.customSql.trim() ||
                isValidatingSql ||
                sqlValidationSuccess
              }
            >
              {sqlButtonLabel}
            </Button>
            {sqlValidationResult && !sqlValidationResult.success && (
              <div className='mt-2 text-danger'>
                <small>{sqlValidationResult.message}</small>
              </div>
            )}
          </Form.Group>
          <div className='frequency-section'>
            <Form.Group>
              <FrequencySection
                frequencyData={formData.actual.frequency}
                onFrequencyChange={(field, value) =>
                  handleInputChange('actual.frequency', field, value)
                }
                label='Frequency Type:'
              />
            </Form.Group>
          </div>
        </div>
      )}

      <div className='form-section'>
        <div className='horizontal-line' />
        <h3 className='section-header-title'>
          Alert timing and destination<span className='asterisk'>*</span>
        </h3>
      </div>

      <div className='form-section'>
        <h4 className='section-title'>Alert between dates:</h4>
        <p className='section-description'>
          {formData.alertType === 'budget'
            ? "Dates are automatically populated from the selected budget. You can modify them as needed, but only within the budget's date range."
            : 'Select the date range for your custom alert.'}
        </p>
        <Form.Group>
          <DatePicker
            range={{ start: formData.startDate, end: formData.endDate }}
            disabled={
              formData.alertType === 'budget'
                ? !formData.budgetId || !formData.startDate || !formData.endDate
                : false
            }
            isOutsideRange={isOutsideRange}
            onChange={({ start, end }) => {
              const newFormData = R.clone({
                ...formData,
                startDate: start || '',
                endDate: end || ''
              });
              onFormDataChange(newFormData);
            }}
          />
          {fieldErrors?.startDate && (
            <small className='form-text text-danger'>{fieldErrors.startDate}</small>
          )}
        </Form.Group>
      </div>

      {fieldErrors?.notificationMethod && (
        <div className='form-section'>
          <small className='form-text text-danger'>{fieldErrors.notificationMethod}</small>
        </div>
      )}

      <div className='form-section'>
        <div className='section-header'>
          <Checkbox
            checked={formData.slack.enabled}
            onChange={e => handleCheckboxChange('slack', e.target.checked)}
            {...SECTION_CHECKBOX_PROPS}
          />
          <h4 className='section-title'>Slack</h4>
          <InfoTooltip size={Size.SM}>
            If you don&apos;t see your slack channel, please create it in Slack first. For security
            and premissioning reasons, we cannot create channels outside of Slack.
          </InfoTooltip>
        </div>
        {formData.slack.enabled && (
          <div className='notification-inputs'>
            <Form.Group>
              <Form.Label>Slack Channel:</Form.Label>
              {isLoadingSlackChannels && (
                <div className='loading-indicator'>
                  <small className='form-text text-muted'>Loading Slack channels...</small>
                </div>
              )}
              {Boolean(slackChannelsError) && (
                <div className='error-message'>
                  <small className='form-text text-danger'>
                    Failed to load Slack channels. Using fallback options.
                  </small>
                </div>
              )}
              <Dropdown
                searchable
                clearable
                ariaLabel='Slack channel'
                placeholder='Select Slack channel...'
                options={slackChannelOptions}
                value={matchOptionValue(slackChannelOptions, formData.slack.channels[0])}
                onChange={channel => {
                  const channels = channel ? [channel] : [];
                  const newFormData = R.clone({
                    ...formData,
                    slack: {
                      ...formData.slack,
                      channels
                    }
                  });
                  onFormDataChange(newFormData);
                }}
                reactSelectProps={{ noOptionsMessage: () => 'No Slack channels available' }}
              />
              {fieldErrors?.slackChannels && (
                <small className='form-text text-danger'>{fieldErrors.slackChannels}</small>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Slack Member(s):</Form.Label>
              {isLoadingSlackMembers && (
                <div className='loading-indicator'>
                  <small className='form-text text-muted'>Loading Slack members...</small>
                </div>
              )}
              {Boolean(slackMembersError) && (
                <div className='error-message'>
                  <small className='form-text text-danger'>
                    Failed to load Slack members. Using fallback options.
                  </small>
                </div>
              )}
              <Dropdown
                searchable
                multiSelect
                showChips
                clearable
                ariaLabel='Slack members'
                placeholder='Select Slack members...'
                options={slackMemberOptions}
                value={matchOptionValues(slackMemberOptions, formData.slack.members)}
                onChange={memberIds => {
                  const newFormData = R.clone({
                    ...formData,
                    slack: {
                      ...formData.slack,
                      members: memberIds
                    }
                  });
                  onFormDataChange(newFormData);
                }}
                reactSelectProps={{ noOptionsMessage: () => 'No Slack members available' }}
              />
              {fieldErrors?.slackMembers && (
                <small className='form-text text-danger'>{fieldErrors.slackMembers}</small>
              )}
            </Form.Group>
          </div>
        )}
      </div>

      <div className='form-section'>
        <div className='section-header'>
          <Checkbox
            checked={formData.email.enabled}
            onChange={e => handleCheckboxChange('email', e.target.checked)}
            {...SECTION_CHECKBOX_PROPS}
          />
          <h4 className='section-title'>Email</h4>
        </div>
        {formData.email.enabled && (
          <Form.Group>
            <Form.Label>Email Address(es):</Form.Label>
            {isLoadingEmailAddresses && (
              <div className='loading-indicator'>
                <small className='form-text text-muted'>Loading email addresses...</small>
              </div>
            )}
            {Boolean(emailAddressError) && (
              <div className='error-message'>
                <small className='form-text text-danger'>
                  Failed to load email addresses. Using fallback options.
                </small>
              </div>
            )}
            <Dropdown
              searchable
              multiSelect
              showChips
              clearable
              ariaLabel='Email addresses'
              placeholder='Select email addresses...'
              options={emailAddressOptions}
              value={matchOptionValues(emailAddressOptions, formData.email.addresses)}
              onChange={emailAddresses => {
                const newFormData = R.clone({
                  ...formData,
                  email: {
                    ...formData.email,
                    addresses: emailAddresses
                  }
                });
                onFormDataChange(newFormData);
              }}
              reactSelectProps={{ noOptionsMessage: () => 'No email addresses available' }}
            />
            {fieldErrors?.emailAddresses && (
              <small className='form-text text-danger'>{fieldErrors.emailAddresses}</small>
            )}
          </Form.Group>
        )}
      </div>
    </Form>
  );
};
